<template>
	<v-chart
		id="chart-container"
		class="chart"
		:option="option"
		autoresize
	/>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, BarChart } from "echarts/charts";
import {
	TitleComponent,
	TooltipComponent,
	DataZoomComponent,
	ToolboxComponent,
	LegendComponent,
	GridComponent,
} from "echarts/components";
import VChart, { THEME_KEY } from "vue-echarts";
import { useTasksStore } from "@/stores/tasks";
import moment from "moment";

use([
	GridComponent,
	CanvasRenderer,
	LineChart,
	BarChart,
	TitleComponent,
	TooltipComponent,
	ToolboxComponent,
	DataZoomComponent,
	LegendComponent,
]);
type PlotData = { date: string; taskTriggers: number };

type ComponentData = {
	plotData: PlotData[];
	taskHistoryDays: number;
	targetYAxisPoints: number;
	possibleYAxisIntervals: number[];
};
export default defineComponent({
	name: "UsageLineChart",
	components: {
		VChart,
	},
	provide: {
		[THEME_KEY]: "light",
	},
	setup() {
		const taskStore = useTasksStore();
		return { taskStore };
	},
	data: (): ComponentData => ({
		plotData: [],
		taskHistoryDays: 7,
		targetYAxisPoints: 6,
		possibleYAxisIntervals: [1, 5, 10, 20, 50, 100, 150, 200, 300, 500, 1000],
	}),
	computed: {
		option(): any {
			return {
				animation: true,
				title: {
					text: "",
					left: "center",
				},
				tooltip: {
					trigger: "axis",
					axisPointer: {
						type: "shadow",
					},
					formatter: (params: any) => {
						const data = params[0];
						const value = data.value;
						const displayValue = value >= 500 ? "500+" : value;
						return `${data.name}<br/># Compute Tasks: ${displayValue}`;
					},
				},
				xAxis: [
					{
						name: "Date (UTC)",
						nameLocation: "middle",
						nameGap: 25,

						type: "category",
						data: this.plotData.map(({ date }) => date),
						axisPointer: {
							type: "shadow",
						},
					},
				],
				yAxis: [
					{
						name: "# Compute Tasks",
						type: "value",
						interval: this.determineYAxisInterval,
						nameLocation: "middle",
						nameGap: 35,
					},
				],
				series: [
					{
						name: "# Compute Tasks",
						type: "bar",
						data: this.plotData.map(({ taskTriggers }) => taskTriggers),
						yAxisIndex: 0,
						itemStyle: {
							color:
								getComputedStyle(document.documentElement)
									.getPropertyValue("--sgnl-charts-chart-first")
									.trim() || "#91cc75",
						},
						showSymbol: false,
						emphasis: { disabled: true },
						select: { disabled: true },
					},
				],
				grid: [
					{
						left: 60,
						right: 30,
						bottom: 45,
						top: 20,
					},
				],
			};
		},
		determineYAxisInterval(): number {
			let largestXAxis = 0;
			this.plotData.forEach((plotPoint) => {
				if (plotPoint.taskTriggers > largestXAxis) {
					largestXAxis = plotPoint.taskTriggers;
				}
			});

			const determinedInterval = Math.ceil(largestXAxis / this.targetYAxisPoints);
			let idealIntervalIndex = 0;

			for (let i = 0; i < this.possibleYAxisIntervals.length - 2; i++) {
				const currentPossibleInterval = this.possibleYAxisIntervals[i];
				const nextPossibleInterval = this.possibleYAxisIntervals[i + 1];
				if (currentPossibleInterval <= determinedInterval && determinedInterval <= nextPossibleInterval) {
					idealIntervalIndex = i + 1;
					break;
				}
			}
			return this.possibleYAxisIntervals[idealIntervalIndex];
		},
	},

	methods: {
		formattedTaskHistory(): void {
			const history = this.taskStore.taskMetadataHistory;
			if (!history || history.length === 0) {
				return;
			}

			// history is [today, yesterday, 2 days ago, ...]
			// We need to reverse it for chronological display and add date labels
			this.plotData = history
				.map((count, index) => {
					const date = moment().subtract(index, "days");
					return {
						date: date.format("YYYY/MM/DD"),
						taskTriggers: count,
					};
				})
				.reverse();
		},
	},
	async created() {
		// fetch partial task history
		await this.taskStore.getTasksMetadataHistoryWithinDays(this.taskHistoryDays);
		this.formattedTaskHistory();
	},
});
</script>

<style scoped>
.chart {
	width: auto;
}
</style>
